import { AiImagePresetStyles, AiMessage, GenerationFile } from "common/aiConstants";
import { IPresentation, IPresentationStorageModel, ISlide, IStorageModel } from "common/interfaces";

import { AmplitudeCategories, trackEvent } from "./amplitude";

export function GeneratePresentation({ presentation: _presentation }: { presentation: IPresentation | IPresentationStorageModel }) {
    const presentation: IPresentation = ("get" in _presentation) ? _presentation.attributes : _presentation;

    const metadata = presentation.metadata ?? {};

    const isPresetImageStyle = AiImagePresetStyles.some(style => style.style === metadata.imageStyle);

    const props = {
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: presentation.id,
        num_slides: Object.keys(presentation.slideRefs).length,
        from_tour: false,
        used_ai_images: metadata.imageSource === "ai",
        model_name: metadata.imageModel,
        custom_style_type: isPresetImageStyle ? "preset" : "prompt",
        custom_style_type_prompt: isPresetImageStyle ? null : metadata.imageStyle,
        image_style: isPresetImageStyle ? metadata.imageStyle : "custom",
        language: metadata.language,
        prompt: metadata.prompt,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch
    };

    trackEvent(AmplitudeCategories.Presentation, "Generate", props);
}

function _getFilesProps(files: GenerationFile[]) {
    const usedFiles = files.filter(file => file.hasBeenUsed);
    return {
        files: usedFiles.map(({ rawFileName, parsedFileName, hasBeenUsedFor }) => ({ rawFileName, parsedFileName, hasBeenUsedFor })),
        files_count: usedFiles.length
    };
}

export function GeneratePresentationOutline({ prompt, files, webSearch }: { prompt: string, files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Generate", props);
}

export function RegeneratePresentationOutline({ prompt, files, webSearch }: { prompt: string, files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Regenerate", props);
}

export function ChatEditPresentationOutline({ prompt, messages, files, webSearch }: { prompt: string, messages: AiMessage[], files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        messages: messages.map(({ role, content }) => ({ role, content })),
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "ChatEdit", props);
}

export function GenerateSlide({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const slide: ISlide = ("get" in _slide) ? _slide.attributes : _slide;

    const metadata = slide.metadata ?? {};

    const slideImage = metadata.generatedModel?.slideImage;

    const isPresetImageStyle = AiImagePresetStyles.some(style => style.style === slideImage.imageStyle);

    const props = {
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: slide.presentationId,
        slide_id: slide.id,
        prompt: metadata.slidePrompt,
        language: metadata.language,
        used_ai_images: slideImage.imageSource === "ai",
        model_name: slideImage.imageModel,
        custom_style_type: isPresetImageStyle ? "preset" : "prompt",
        custom_style_type_prompt: isPresetImageStyle ? null : slideImage.imageStyle,
        image_style: isPresetImageStyle ? slideImage.imageStyle : "custom",
        context_source: metadata.files?.length ? "file" : undefined,
        use_presentation_as_context: metadata.presentationAsContext,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch
    };
    trackEvent(AmplitudeCategories.Slide, "Generate", props);
}
